import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../services/api';

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);

  if (!user || dismissed) return null;
  // Backend returns snake_case after middleware; tolerate either shape.
  const verified = user.email_verified ?? user.emailVerified;
  if (verified !== false) return null;

  const handleResend = async () => {
    if (!user.email || resending) return;
    setResending(true);
    try {
      const response = await auth.resendVerification({ email: user.email });
      toast.success(response.data?.message || 'Verification email sent.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resend verification.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="border-b border-amber-500/30 bg-amber-500/10">
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-3 sm:items-center sm:px-6 lg:px-8">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-300" />
        <p className="flex-1 text-sm text-amber-100">
          Verify your email to activate all features. We sent a link to{' '}
          <span className="font-semibold">{user.email}</span>.
        </p>
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="text-sm font-semibold text-amber-200 underline-offset-2 hover:underline disabled:opacity-60"
        >
          {resending ? 'Sending…' : 'Resend'}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-amber-200 hover:text-amber-100"
          aria-label="Dismiss verification banner"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
